import { useCallback, useContext, useState } from 'react'

import type { FC } from 'react'

import AstaModal from '@/components/shared/AstaModal/AstaModal'
import CopyAndMoveForm from '@/components/shared/CopyAndMove/CopyAndMoveForm'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { AuthContext } from '@/contexts/auth'
import { WithSelectedApp } from '@/contexts/selectedApp'
import type { SelectedAppProps } from '@/contexts/selectedApp/WithSelectedApp'
import { useAssets } from '@/hooks/App/useAssets'
import { canCreate } from '@/pages/Admin/helpers'
import type { Asset, AssetWithParent } from '@/shared/types'

interface Props {
	type: Asset['type']
	asset: AssetWithParent
	onClose?: () => void
}

const DuplicateAssetMenu: FC<Props & SelectedAppProps> = ({
	selectedApp,
	type,
	asset,
	onClose,
}) => {
	const [open, setOpen] = useState(false)

	const { user } = useContext(AuthContext)
	const { createAsset } = useAssets(type)

	const onCopy = useCallback(
		(name: string, newParent: string) => {
			const { _id, ...rest } = asset

			createAsset.mutate({
				pathParams: {
					appId: newParent,
				},
				body: {
					...rest,
					name,
					tags: asset.tags.map(tag =>
						typeof tag === 'string' ? tag : tag._id
					),
					parent: newParent,
				},
			})
			if (onClose) onClose()
		},
		[asset, createAsset, onClose]
	)

	const _onOpen = useCallback(() => {
		setOpen(true)
	}, [])

	const _onClose = useCallback(() => {
		setOpen(false)
		if (onClose) onClose()
	}, [onClose])

	if (!canCreate(asset.parent.role) && !user?.isAdmin) return null

	return (
		<>
			<DropdownMenuItem
				onClick={e => {
					e.preventDefault()
					e.stopPropagation()
					_onOpen()
				}}
			>
				Copy to
			</DropdownMenuItem>
			<AstaModal onClose={_onClose} isOpen={open}>
				<CopyAndMoveForm
					selectedApp={selectedApp}
					onSave={onCopy}
					onClose={_onClose}
					name={asset.name}
					isAdmin={user?.isAdmin}
					type="copy"
					resource={asset}
				/>
			</AstaModal>
		</>
	)
}

export default WithSelectedApp({})(DuplicateAssetMenu)
